import React, { useMemo, useState } from 'react';
import Field from './Field';
import {
  CALLING_CODES, phoneError, phoneHint, regionForPhone, withCallingCode,
} from '../domain/phone';
import s from './PhoneField.module.css';

// A phone number, entered in international format.
//
// A knowing divergence from MMS, whose phone fields are bare unvalidated text
// boxes that will store `banana`; src/domain/phone.js has the reasoning and
// what was chosen not to build alongside it.
//
// ── The select does not hold a country ──────────────────────────────────────
//
// It is derived from the text on every render and edits the text when used,
// so there is no second piece of state to fall out of step with the box. A
// stored country combined with a typed national number would turn a pasted
// British number under a selector still saying United States into a valid
// American number belonging to a stranger; here a pasted +44 number moves the
// dropdown to the United Kingdom.
//
// Choosing a country is a shortcut for typing the code: it puts `+44` at the
// front of the box, replacing a code already there rather than stacking one.

// CLDR region names, localised, rather than hand-maintained English ones, as
// domain/locale.js derives the date format from Intl. The code is shown beside
// the name because the code is what is being chosen.
function regionNamer() {
  try {
    const names = new Intl.DisplayNames(undefined, { type: 'region' });
    return (r) => names.of(r) || r;
  } catch {
    // A guard: Intl.DisplayNames is in every supported browser and Node 18+. A
    // dropdown of ISO codes is degraded; one that throws on render takes the
    // wizard with it.
    return (r) => r;
  }
}

export default function PhoneField({
  id, label = 'Phone Number', hint, value, onChange,
  // A message the form wants shown, overriding the blur rule below (#120). The
  // edit drawers refuse on submit, and a teacher who presses Save with the
  // cursor still in this box would otherwise see a refusal with nothing
  // explaining it (#115). The wizard's refusal is a disabled Next instead.
  error: forced = null,
}) {
  const options = useMemo(() => {
    const nameOf = regionNamer();
    return Object.keys(CALLING_CODES)
      .map((region) => ({ region, label: `${nameOf(region)} (+${CALLING_CODES[region]})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Shown once the teacher has left the field, not while they are in it:
  // choosing a country leaves "+44" in the box, which is not yet a number, and
  // a message scolding mid-word gets read as noise. It is the only place the
  // teacher is told what shape is wanted (#90). `canAdvance` in
  // hooks/useAddStudentForm.js holds Next shut from the first keystroke, and
  // pressing Next blurs the input, so the reason appears when the refusal is
  // felt.
  const [touched, setTouched] = useState(false);

  const region = regionForPhone(value);
  // The form's message wins when there is one. It is the same sentence —
  // `studentEditErrors` reaches `phoneError` too — so `forced` changes only
  // when it is on screen.
  const error = forced || (touched ? phoneError(value) : null);

  return (
    <Field
      label={label}
      htmlFor={id}
      error={error}
      // The hint (#90): it narrows once a country is known, because "+44 then
      // the number, drop the leading 0" is advice and "international format" is
      // a category.
      //
      // "Optional." is not a prop: every phone field in this app is optional
      // (`optionalPhone` in firestore.rules), as in MMS.
      hint={error ? undefined : (hint || `Optional. ${phoneHint(region)}`)}
    >
      <div className={s.row}>
        <select
          aria-label={`${label} country`}
          value={region || ''}
          onChange={(e) => onChange(withCallingCode(value, e.target.value))}
        >
          {/* Present when the box is empty or holds a code not listed, and
              selectable so a teacher can get back to a bare box. Choosing it
              leaves the text alone: withCallingCode does not recognise '' as a
              region. */}
          <option value="">Country…</option>
          {options.map((o) => <option key={o.region} value={o.region}>{o.label}</option>)}
        </select>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          data-error={error ? 'true' : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
        />
      </div>
    </Field>
  );
}
